import { useEffect } from 'react';

import {
  failOperation,
  type OperationFailureReason,
  startOperation,
  succeedOperation,
} from '@mint-lib/datadog';

const PENDING_KEY = 'connections.pendingOperation';

export type ConnectionOperationName = 'connection.add' | 'connection.reconnect';

export interface ConnectionOperationContext {
  /** Platform URN, as in the route's `$integration` param. */
  platform: string;
  /** `oauth` leaves the app for the provider and ends after the reload; `basic` ends on the same page. */
  flow: 'oauth' | 'basic';
  /** Whether the flow runs on the integration registry or on the legacy `@myn/connections` pages. */
  registry: boolean;
}

// The Datadog calls per operation, with the name as a literal so it stays
// searchable (the `@mint/datadog-operation-name` lint rule).
const OPERATIONS = {
  'connection.add': {
    start: (context: ConnectionOperationContext) =>
      startOperation('connection.add', { context: { ...context } }),
    succeed: () => succeedOperation('connection.add'),
    fail: (reason: OperationFailureReason) =>
      failOperation('connection.add', reason),
  },
  'connection.reconnect': {
    start: (context: ConnectionOperationContext) =>
      startOperation('connection.reconnect', { context: { ...context } }),
    succeed: () => succeedOperation('connection.reconnect'),
    fail: (reason: OperationFailureReason) =>
      failOperation('connection.reconnect', reason),
  },
} satisfies Record<ConnectionOperationName, unknown>;

const isConnectionOperationName = (
  value: string | null,
): value is ConnectionOperationName =>
  value !== null && Object.hasOwn(OPERATIONS, value);

const getPending = (): ConnectionOperationName | null => {
  try {
    const pending = sessionStorage.getItem(PENDING_KEY);
    return isConnectionOperationName(pending) ? pending : null;
  } catch {
    return null;
  }
};

const setPending = (name: ConnectionOperationName | null) => {
  try {
    if (name) {
      sessionStorage.setItem(PENDING_KEY, name);
    } else {
      sessionStorage.removeItem(PENDING_KEY);
    }
  } catch {
    // Storage unavailable: the end after the OAuth reload is lost and Datadog
    // ends the operation as `timeout`.
  }
};

/**
 * Starts a connection RUM operation. The OAuth flows leave the app
 * (`location.assign`) and come back on a new page load, so which operation is
 * open is kept in sessionStorage (per tab) rather than in React state.
 *
 * The add and reconnect flows return through the same OAuth callback
 * handlers, which call the end functions of both. The stored name is what
 * keeps them apart: an end call only ends the operation of its own name, so
 * a reconnect's callback never ends an add and the other way round. One slot
 * means at most one connection operation is open per tab; starting any of
 * them abandons whichever one is still open (a resubmit after the browser
 * restored the page from the bfcache, or a reconnect started after an add
 * whose end was lost).
 */
export function startConnectionOperation(
  name: ConnectionOperationName,
  context: ConnectionOperationContext,
) {
  const pending = getPending();
  if (pending) {
    OPERATIONS[pending].fail('abandoned');
  }
  setPending(name);
  OPERATIONS[name].start(context);
}

/** Ends the open operation as succeeded, if it is `name`. */
export function succeedConnectionOperation(name: ConnectionOperationName) {
  if (getPending() !== name) return;
  setPending(null);
  OPERATIONS[name].succeed();
}

/** Ends the open operation as failed, if it is `name`. */
export function failConnectionOperation(
  name: ConnectionOperationName,
  reason: OperationFailureReason,
) {
  if (getPending() !== name) return;
  setPending(null);
  OPERATIONS[name].fail(reason);
}

const hasOAuthCallbackParams = (): boolean => {
  const search = new URLSearchParams(globalThis.location?.search ?? '');
  return (
    search.has('oauth_result') || (search.has('state') && search.has('code'))
  );
};

/**
 * Call on the add and update connection pages. When a page opens without an
 * OAuth callback in the URL while an add or a reconnect is still open, the
 * user came back without finishing it (browser back from the provider, or
 * navigated away mid-wizard), so it is failed as `abandoned`. A user who
 * never comes back is ended by Datadog as `timeout` when the session expires.
 *
 * CM's OAuth `redirect_uri` always points at the add route; for a reconnect
 * the route's `beforeLoad` redirects to the update route before either page
 * mounts, and keeps `state`/`code` in the search, so this does not misfire.
 */
export function useAbandonUnfinishedConnectionOperation() {
  useEffect(() => {
    if (hasOAuthCallbackParams()) return;
    const pending = getPending();
    if (pending) {
      setPending(null);
      OPERATIONS[pending].fail('abandoned');
    }
  }, []);
}
